import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Icon } from '@iconify/react';
import SEO from '../components/SEO';
import './BlogService.css';

const BlogService = () => {
  const contactRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    industry: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const scrollToContact = () => {
    contactRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const webhookUrl = 'https://bshong-n8n.ngrok.io/webhook/9f9f0452-6c8d-401e-8198-29d5d5678861';
      
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          contact: formData.contact,
          industry: formData.industry,
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error(`웹훅 호출 실패: ${response.status}`);
      }

      setSubmitStatus('success');
      setFormData({ name: '', contact: '', industry: '' });
      
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } catch (error) {
      console.error('상담 신청 오류:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: '블로그 서비스 - FLOWTO.ai',
    description: 'AI 기반 블로그 콘텐츠 생성 서비스',
    provider: {
      '@type': 'Organization',
      name: 'FLOWTO.ai',
      url: 'https://flowto-ai.com'
    },
    areaServed: 'KR',
    serviceType: 'ContentCreation'
  };

  return (
    <div className="blog-service-page">
      <SEO
        title="블로그 서비스"
        description="AI 기반 블로그 콘텐츠 생성 서비스"
        url="/blog-service"
        type="website"
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <HeroSection onCTAClick={scrollToContact} />

      {/* Process Section */}
      <ProcessSection />

      {/* Use Cases Section */}
      <UseCasesSection />

      {/* Features Section */}
      <FeaturesSection />

      {/* Pricing Section */}
      <PricingSection />

      {/* Contact Section */}
      <ContactSection
        ref={contactRef}
        formData={formData}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        submitStatus={submitStatus}
      />
    </div>
  );
};

// Hero Section Component
const HeroSection = ({ onCTAClick }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section className="blog-hero-section">
      <div className="blog-hero-container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="blog-hero-content"
        >
          <h1 className="blog-hero-title">
            블로그 포스팅에 더 이상<br />
            시간을 허비하지 마세요
          </h1>
          <p className="blog-hero-subtitle">
            AI 자동화와 전문가의 검수로 완성하는 블로그.<br />
            빠르고 정확한 포스팅으로 마케팅 걱정을 끝내세요.
          </p>
          <button className="blog-cta-button" onClick={onCTAClick}>
            무료 상담 신청하기
          </button>
        </motion.div>
      </div>
    </section>
  );
};

// Process Section Component
const ProcessSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const steps = [
    {
      icon: 'solar:upload-bold',
      title: 'Step 1',
      description: '간단한 메모와 사진',
      detail: '작성하고 싶은 내용과 관련 사진들을 보내주세요.'
    },
    {
      icon: 'solar:document-bold',
      title: 'Step 2',
      description: '초안 생성 및 전문가 검수',
      detail: 'AI가 초안을 작성하고, 전문가가 꼼꼼히 검수하여 완성도 높은 글을 만듭니다.'
    },
    {
      icon: 'solar:magnifer-bold',
      title: 'Step 3',
      description: 'SEO 최적화 업로드',
      detail: '키워드 분석과 SEO 최적화를 거쳐 블로그에 업로드합니다.'
    },
    {
      icon: 'solar:check-circle-bold',
      title: 'Step 4',
      description: '최종 확인 및 보완',
      detail: '클라이언트 검토 및 수정 보완해서 최종 결과물을 만듭니다.'
    }
  ];

  return (
    <section className="blog-process-section">
      <div className="blog-container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="blog-section-title">작업 프로세스</h2>
          <div className="blog-process-grid">
            {steps.map((step, index) => {
              return (
                <motion.div
                  key={index}
                  className="blog-process-card"
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                >
                  <div className="blog-process-icon-wrapper">
                    <Icon icon={step.icon} width={48} height={48} />
                  </div>
                  <h3 className="blog-process-title">{step.title}</h3>
                  <h4 className="blog-process-subtitle">{step.description}</h4>
                  <p className="blog-process-detail">{step.detail}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Use Cases Section Component
const UseCasesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const useCases = [
    {
      icon: 'solar:home-bold',
      title: '인테리어',
      description: '전/후 비교',
      detail: '인테리어 전후 사진과 함께 고객의 만족도를 보여주는 블로그 포스팅으로 신뢰도를 높이세요.'
    },
    {
      icon: 'solar:graduation-cap-bold',
      title: '교육/학원',
      description: '커리큘럼 소개',
      detail: '교육 과정과 커리큘럼을 체계적으로 소개하여 학부모와 학생들의 관심을 끌어보세요.'
    },
    {
      icon: 'solar:fork-knife-bold',
      title: '맛집/제품',
      description: '상세 리뷰',
      detail: '맛집이나 제품에 대한 상세한 리뷰와 후기를 통해 고객의 구매 결정을 돕습니다.'
    }
  ];

  return (
    <section className="blog-usecases-section">
      <div className="blog-container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="blog-section-title">이런 분들께 추천합니다</h2>
          <p className="blog-usecases-footer">
            지속적인 관리가 필요한 모든 분 대환영
          </p>
          <div className="blog-usecases-grid">
            {useCases.map((useCase, index) => {
              return (
                <motion.div
                  key={index}
                  className="blog-usecase-card"
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                >
                  <div className="blog-usecase-icon-wrapper">
                    <Icon icon={useCase.icon} width={40} height={40} />
                  </div>
                  <h3 className="blog-usecase-title">{useCase.title}</h3>
                  <p className="blog-usecase-description">{useCase.description}</p>
                  <p className="blog-usecase-detail">{useCase.detail}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Features Section Component
const FeaturesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const features = [
    {
      icon: 'solar:bolt-bold',
      title: '자동화로 비용 절감',
      description: 'AI 기반 자동화로 인건비를 절감하고 효율적인 블로그 운영이 가능합니다.'
    },
    {
      icon: 'solar:global-bold',
      title: '다양한 플랫폼 지원',
      description: '네이버 블로그, 티스토리 등 다양한 플랫폼에 최적화된 콘텐츠를 제공합니다.'
    },
    {
      icon: 'solar:chart-2-bold',
      title: 'SEO 최적화',
      description: '키워드 분석과 SEO 최적화를 통해 검색 노출을 극대화합니다.'
    },
    {
      icon: 'solar:check-circle-bold',
      title: '사람의 꼼꼼한 검수',
      description: 'AI가 작성한 초안을 전문가가 직접 검수하여 완성도 높은 콘텐츠를 보장합니다.'
    }
  ];

  return (
    <section className="blog-features-section">
      <div className="blog-container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="blog-section-title">왜 더 빠르고 정확할까요?</h2>
          <div className="blog-features-grid">
            {features.map((feature, index) => {
              return (
                <motion.div
                  key={index}
                  className="blog-feature-card"
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                >
                  <div className="blog-feature-icon-wrapper">
                    <Icon icon={feature.icon} width={40} height={40} />
                  </div>
                  <h3 className="blog-feature-title">{feature.title}</h3>
                  <p className="blog-feature-description">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Pricing Section Component
const PricingSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section className="blog-pricing-section">
      <div className="blog-container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="blog-pricing-card"
        >
          <h2 className="blog-pricing-title">Standard Plan</h2>
          <div className="blog-pricing-amount">
            <span className="blog-pricing-currency">월</span>
            <span className="blog-pricing-price">500,000</span>
            <span className="blog-pricing-unit">원</span>
          </div>
          <div className="blog-pricing-features">
            <div className="blog-pricing-feature-item">
              <Icon icon="solar:check-circle-bold" width={20} height={20} />
              <span>월 10건 포스팅</span>
            </div>
            <div className="blog-pricing-feature-item">
              <Icon icon="solar:gallery-bold" width={20} height={20} />
              <span>수정 요청 사항 반영</span>
            </div>
            <div className="blog-pricing-feature-item">
              <Icon icon="solar:check-circle-bold" width={20} height={20} />
              <span>1년 계약시 첫 달 무료</span>
            </div>
            <div className="blog-pricing-feature-item">
              <Icon icon="solar:check-circle-bold" width={20} height={20} />
              <span>첫 1회 테스트 블로그 작성 후 계약 진행 가능</span>
            </div>
          </div>
          <p className="blog-pricing-note">기타 조건 협의 가능</p>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section Component
const ContactSection = React.forwardRef(({ formData, handleChange, handleSubmit, isSubmitting, submitStatus }, ref) => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  React.useImperativeHandle(ref, () => sectionRef.current);

  return (
    <section ref={sectionRef} className="blog-contact-section">
      <div className="blog-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="blog-contact-content"
        >
          <h2 className="blog-section-title">상담 신청</h2>
          <p className="blog-contact-description">
            블로그 제작 서비스에 관심이 있으시다면 아래 폼을 작성해주세요.<br />
            빠른 시일 내에 연락드리겠습니다.
          </p>
          <form className="blog-contact-form" onSubmit={handleSubmit}>
            <div className="blog-form-group">
              <label htmlFor="name">이름</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="이름을 입력해주세요"
              />
            </div>
            <div className="blog-form-group">
              <label htmlFor="contact">연락처</label>
              <input
                type="tel"
                id="contact"
                name="contact"
                value={formData.contact}
                onChange={handleChange}
                required
                placeholder="연락처를 입력해주세요"
              />
            </div>
            <div className="blog-form-group">
              <label htmlFor="industry">업종</label>
              <input
                type="text"
                id="industry"
                name="industry"
                value={formData.industry}
                onChange={handleChange}
                required
                placeholder="업종을 입력해주세요"
              />
            </div>
            {submitStatus === 'success' && (
              <div className="blog-form-success">
                상담 신청이 완료되었습니다. 빠른 시일 내에 연락드리겠습니다.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="blog-form-error">
                오류가 발생했습니다. 다시 시도해주세요.
              </div>
            )}
            <button type="submit" className="blog-submit-button" disabled={isSubmitting}>
              {isSubmitting ? '전송 중...' : '상담 신청하기'}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
});

ContactSection.displayName = 'ContactSection';

export default BlogService;

